import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { populationsApi } from '@/lib/api'
import type { OrgMember } from '@/lib/api'
import type { AudienceAttributionMode } from '@/types/components/audiences/audience-sharing'

interface AttributionPanelProps {
  audienceId: string
  members: Array<OrgMember>
  organizationName?: string | null
  currentOwnerId?: string
  initialSharedAs?: AudienceAttributionMode
  initialSharedAsUserId?: string | null
}

export function AttributionPanel({
  audienceId,
  members,
  organizationName,
  currentOwnerId,
  initialSharedAs = 'member',
  initialSharedAsUserId,
}: AttributionPanelProps) {
  const { t } = useTranslation('app')
  const [sharedAs, setSharedAs] = useState<AudienceAttributionMode>(initialSharedAs)
  const [changeOwnerTo, setChangeOwnerTo] = useState<string>('')
  const [savingAttribution, setSavingAttribution] = useState(false)
  const [savingOwner, setSavingOwner] = useState(false)
  const [isAttributionOpen, setIsAttributionOpen] = useState(false)
  const [isTransferOpen, setIsTransferOpen] = useState(false)

  const selectedMemberName = members.find((m) => m.id === (initialSharedAsUserId ?? currentOwnerId))?.name ?? 'Your name'
  const resolvedOrganizationName = organizationName?.trim() || 'your organization'
  const previewLabel = sharedAs === 'brand'
    ? resolvedOrganizationName
    : `${selectedMemberName} from ${resolvedOrganizationName}`

  async function handleBrandToggle(checked: boolean) {
    const nextSharedAs: AudienceAttributionMode = checked ? 'brand' : 'member'
    const previousSharedAs = sharedAs

    setSharedAs(nextSharedAs)
    setSavingAttribution(true)
    try {
      await populationsApi.updateOwnership(audienceId, {
        sharedAs: nextSharedAs,
        sharedAsUserId: nextSharedAs === 'member' ? (initialSharedAsUserId ?? undefined) : null,
      })
      toast.success(t('audienceActions.attributionUpdated'))
    } catch {
      setSharedAs(previousSharedAs)
      toast.error(t('audienceActions.attributionFailed'))
    } finally {
      setSavingAttribution(false)
    }
  }

  async function handleTransferOwner() {
    if (!changeOwnerTo) return
    setSavingOwner(true)
    try {
      await populationsApi.updateOwnership(audienceId, { changeOwnerTo })
      toast.success(t('sharing.ownershipTransferred', { name: members.find((m) => m.id === changeOwnerTo)?.name ?? '' }))
      setChangeOwnerTo('')
    } catch {
      toast.error(t('sharing.transferFailed'))
    } finally {
      setSavingOwner(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Attribution identity */}
      <div className="flex flex-col gap-2 rounded-lg border border-border px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text size="sm" weight="medium">{t('audienceActions.shownAs')}</Text>
            <span
              data-testid="shared-as-preview"
              className="truncate text-xs text-muted-foreground"
            >
              {previewLabel}
            </span>
          </div>
          <button
            type="button"
            data-testid="edit-shared-as"
            onClick={() => setIsAttributionOpen((current) => !current)}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            {isAttributionOpen ? t('done', { ns: 'common' }) : t('edit', { ns: 'common' })}
          </button>
        </div>

        {isAttributionOpen && (
          <div className="flex items-center justify-between gap-3 border-t border-border pt-2">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">{t('audienceActions.useBrandOnly')}</span>
              <span className="text-xs text-muted-foreground">Hide personal name in partner invites</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {savingAttribution && (
                <span className="text-xs text-muted-foreground">{t('saving', { ns: 'common' })}</span>
              )}
              <Switch
                data-testid="shared-as-brand"
                checked={sharedAs === 'brand'}
                onCheckedChange={handleBrandToggle}
                disabled={savingAttribution}
              />
            </div>
          </div>
        )}
      </div>

      {/* Transfer ownership */}
      <div className="flex flex-col gap-2 rounded-lg border border-border px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text size="sm" weight="medium">{t('sharing.transferOwnership')}</Text>
            <span className="truncate text-xs text-muted-foreground">
              Make another team member the owner
            </span>
          </div>
          <button
            type="button"
            data-testid="edit-transfer-owner"
            onClick={() => setIsTransferOpen((current) => !current)}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            {isTransferOpen ? t('done', { ns: 'common' }) : t('edit', { ns: 'common' })}
          </button>
        </div>

        {isTransferOpen && (
          <div className="flex flex-col gap-2 border-t border-border pt-2">
            <select
              value={changeOwnerTo}
              onChange={(e) => setChangeOwnerTo(e.target.value)}
              data-testid="change-owner-select"
              className="w-full rounded-md border border-muted-foreground/30 bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="">{t('sharing.selectNewOwner')}</option>
              {members
                .filter((m) => m.id !== currentOwnerId)
                .map((m) => (
                  <option key={m.id} value={m.id}>{m.name ?? m.email}</option>
                ))}
            </select>

            <Button
              type="button"
              data-testid="transfer-owner-btn"
              onClick={handleTransferOwner}
              disabled={!changeOwnerTo || savingOwner}
              variant="default"
              className="self-start rounded-full"
            >
              {savingOwner ? t('transferring', { ns: 'common' }) : t('transfer', { ns: 'common' })}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
